import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, FileText, Download, Building2 } from "lucide-react";
import FileUploader from "@/components/site/FileUploader";

const EMPTY_PROP = {
  title: "", location: "OMR", address: "",
  property_type: "apartment", bhk: "2BHK", area_sqft: 1000, furnishing: "Semi-Furnished",
  status: "for_rent", price: 25000, budget_band: "25k_50k",
  image_url: "https://images.pexels.com/photos/32355381/pexels-photo-32355381.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  badges: ["Verified by Hi-Tech Realty"], verified: true, description: "",
};

const LOCATIONS = ["OMR", "ECR", "Velachery", "Anna Nagar", "Adyar", "Tambaram", "Porur", "Sholinganallur", "T. Nagar"];
const TYPES = ["apartment", "independent_house", "villa", "commercial", "plot"];
const STATUSES = ["for_rent", "for_sale", "managed"];
const BUDGETS = ["below_25k", "25k_50k", "50k_1l", "1l_plus"];
const BHKS = ["", "1BHK", "2BHK", "3BHK", "4BHK"];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("properties");

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="page-admin" className="container-x py-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="eyebrow">Admin Console</div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold text-brand-navy">Welcome, {user?.name}.</h1>
          <p className="mt-1 text-sm text-brand-slate/70">Manage property listings and upload inspection reports.</p>
        </div>
        <button data-testid="admin-logout" onClick={doLogout} className="btn-ghost-navy"><LogOut size={14} /> Logout</button>
      </div>

      <div className="mt-8 inline-flex border border-brand-border bg-white text-xs uppercase tracking-[0.2em] font-semibold">
        {[
          { k: "properties", l: "Properties" },
          { k: "reports", l: "Inspection Reports" },
        ].map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-5 py-2 ${tab === t.k ? "bg-brand-navy text-white" : "text-brand-navy hover:text-brand-cyan"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "properties" ? <PropertiesAdmin /> : <ReportsAdmin />}
      </div>
    </div>
  );
}

/* ----- Properties admin ----- */
function PropertiesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | EMPTY_PROP | existing prop
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/properties");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...editing };
      payload.area_sqft = payload.area_sqft ? Number(payload.area_sqft) : null;
      payload.price = Number(payload.price) || 0;
      if (payload.id) {
        await api.put(`/properties/${payload.id}`, payload);
        toast.success("Property updated.");
      } else {
        await api.post("/properties", payload);
        toast.success("Property added.");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="eyebrow">All Properties ({items.length})</div>
        <button data-testid="admin-add-property" onClick={() => setEditing({ ...EMPTY_PROP })} className="btn-cyan"><Plus size={14}/> Add Property</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <article key={p.id} data-testid={`admin-prop-${p.id}`} className="card-flat overflow-hidden flex flex-col">
            <img src={p.image_url} alt={p.title} className="w-full h-40 object-cover" />
            <div className="p-4 flex-1 flex flex-col">
              <div className="font-semibold text-brand-navy text-sm">{p.title}</div>
              <div className="text-xs text-brand-slate/70 mt-1">{p.location} · {p.property_type} · {p.bhk || "—"}</div>
              <div className="text-xs text-brand-slate/70 mt-1">₹{p.price.toLocaleString("en-IN")} · {p.status}</div>
              <div className="mt-3 flex gap-2">
                <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing(p)} className="btn-ghost-navy flex-1 !py-2 !text-[10px]"><Pencil size={12}/> Edit</button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)} className="btn-ghost-navy flex-1 !py-2 !text-[10px] !border-red-300 !text-red-600 hover:!bg-red-500 hover:!text-white hover:!border-red-500"><Trash2 size={12}/> Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-brand-navy/70 z-50 grid place-items-center p-4 overflow-y-auto" data-testid="admin-property-modal">
          <form onSubmit={save} className="bg-white w-full max-w-3xl p-6 md:p-8 my-10">
            <div className="flex items-center justify-between border-b border-brand-border pb-4">
              <div className="font-display text-2xl font-bold text-brand-navy">{editing.id ? "Edit" : "Add"} Property</div>
              <button type="button" onClick={() => setEditing(null)} data-testid="pf-cancel" className="text-brand-slate/60 hover:text-red-500 text-sm">Cancel</button>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} required testid="pf-title" />
              <Field label="Address" value={editing.address} onChange={(v) => setEditing({ ...editing, address: v })} required testid="pf-address" />
              <Select label="Location" value={editing.location} options={LOCATIONS} onChange={(v) => setEditing({ ...editing, location: v })} testid="pf-location" />
              <Select label="Type" value={editing.property_type} options={TYPES} onChange={(v) => setEditing({ ...editing, property_type: v })} testid="pf-type" />
              <Select label="BHK" value={editing.bhk || ""} options={BHKS} onChange={(v) => setEditing({ ...editing, bhk: v || null })} testid="pf-bhk" />
              <Field label="Area (sqft)" type="number" value={editing.area_sqft ?? ""} onChange={(v) => setEditing({ ...editing, area_sqft: v })} required={false} testid="pf-area" />
              <Field label="Furnishing" value={editing.furnishing || ""} onChange={(v) => setEditing({ ...editing, furnishing: v })} required={false} testid="pf-furnish" />
              <Select label="Status" value={editing.status} options={STATUSES} onChange={(v) => setEditing({ ...editing, status: v })} testid="pf-status" />
              <Field label="Price (₹)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} required testid="pf-price" />
              <Select label="Budget Band" value={editing.budget_band} options={BUDGETS} onChange={(v) => setEditing({ ...editing, budget_band: v })} testid="pf-budget" />
              <Field label="Image URL" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} required className="sm:col-span-2" testid="pf-image" />
              <Field label="Badges (comma-separated)" value={(editing.badges || []).join(", ")}
                onChange={(v) => setEditing({ ...editing, badges: v.split(",").map((s) => s.trim()).filter(Boolean) })}
                required={false} className="sm:col-span-2" testid="pf-badges" />
              <label className="block sm:col-span-2">
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Description</span>
                <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan"
                  data-testid="pf-description" />
              </label>
            </div>
            <button disabled={busy} data-testid="pf-save" className="btn-cyan mt-5">{busy ? "Saving…" : "Save Property"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ----- Inspection Reports admin ----- */
function ReportsAdmin() {
  const [reports, setReports] = useState([]);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    owner_email: "owner@demo.in",
    title: "Monthly Inspection — Velachery Apartment",
    date: new Date().toISOString().slice(0, 10),
    inspector: "Ravi Kumar",
    summary: "All clear. Minor seepage near balcony — vendor scheduled for next week.",
    health_score: "AMBER",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/inspection-reports");
    setReports(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        file_url: files.find((f) => (f.name || "").toLowerCase().endsWith(".pdf"))?.url || null,
        photo_urls: files.filter((f) => !(f.name || "").toLowerCase().endsWith(".pdf")).map((f) => f.url),
      };
      await api.post("/admin/inspection-reports", payload);
      toast.success("Inspection report uploaded.");
      setFiles([]);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't upload"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="card-flat p-6" data-testid="admin-report-form">
        <div className="eyebrow">Upload New Report</div>
        <h3 className="mt-2 font-display text-2xl font-bold text-brand-navy">Field-verified inspection report.</h3>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Owner Email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} required testid="rf-email" />
          <Field label="Inspector" value={form.inspector} onChange={(v) => setForm({ ...form, inspector: v })} required testid="rf-inspector" />
          <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required className="sm:col-span-2" testid="rf-title" />
          <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testid="rf-date" />
          <Select label="Health Score" value={form.health_score} options={["GREEN", "AMBER", "RED"]} onChange={(v) => setForm({ ...form, health_score: v })} testid="rf-health" />
          <label className="block sm:col-span-2">
            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Summary</span>
            <textarea required rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
              className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" data-testid="rf-summary" />
          </label>
          <div className="sm:col-span-2">
            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">Attach PDF + Photos</span>
            <div className="mt-2"><FileUploader testId="report-uploader" onChange={setFiles} /></div>
          </div>
        </div>
        <button disabled={busy} data-testid="rf-submit" className="btn-cyan mt-5">{busy ? "Uploading…" : "Publish Report to Owner"}</button>
      </form>

      <div className="card-flat p-6">
        <div className="flex items-center justify-between">
          <div className="eyebrow">All Reports ({reports.length})</div>
          <Building2 size={18} className="text-brand-cyan" />
        </div>
        {reports.length === 0 ? (
          <p className="mt-4 text-sm text-brand-slate/70">No reports uploaded yet.</p>
        ) : (
          <ul className="mt-5 space-y-3 max-h-[640px] overflow-y-auto">
            {reports.map((r) => (
              <li key={r.id} className="border border-brand-border p-4" data-testid={`admin-report-${r.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-brand-navy text-sm">{r.title}</div>
                    <div className="text-xs text-brand-slate/70 mt-1">{r.date} · {r.inspector} · {r.owner_email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 border ${r.health_score === "GREEN" ? "text-emerald-600 bg-emerald-50 border-emerald-200" : r.health_score === "AMBER" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200"}`}>{r.health_score}</span>
                </div>
                <p className="text-xs text-brand-slate/70 mt-2">{r.summary}</p>
                {r.file_url && (
                  <a href={fileUrl(r.file_url)} target="_blank" rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-brand-cyan hover:underline">
                    <Download size={12}/> PDF
                  </a>
                )}
                {(r.photo_urls || []).length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {r.photo_urls.map((u, i) => (
                      <a key={i} href={fileUrl(u)} target="_blank" rel="noreferrer"
                        className="border border-brand-border px-2 py-0.5 text-[10px] text-brand-navy hover:text-brand-cyan">photo {i + 1}</a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = true, className = "", testid }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan" />
    </label>
  );
}
function Select({ label, value, options, onChange, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-navy">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-2 w-full border border-brand-border bg-white px-3 py-3 text-sm focus:outline-none focus:border-brand-cyan">
        {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </label>
  );
}
